import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';

import { __t, PAYMENT_PROVIDERS } from '@mssgme/shared';
import { LandingsPropTypes } from '@mssgme/ui';
import { ThemedButton } from '../../../UI';
import { useCRMData, useModalThemes } from '../../../hooks';

// paypal buttons docs: https://paypal.github.io/react-paypal-js

import styles from './PayPalPayment.scss';

const propTypes = {
    order: LandingsPropTypes.order.isRequired,
    onPay: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

export function PayPalPayment({
    order: {
        total,
        items,
        contactInfo: { firstName, lastName },
    },
    disabled,
    onPay,
}) {
    const { icon: Icon, label, supported_currencies } = PAYMENT_PROVIDERS.paypal;
    const { payments } = useCRMData();
    const { contentStyle } = useModalThemes();
    const isSupportedCurrency = supported_currencies.includes(total.currency);

    const createOrder = useCallback(
        (data, actions) =>
            actions.order.create({
                purchase_units: [
                    {
                        amount: {
                            currency_code: total.currency,
                            value: total.amount,
                            breakdown: {
                                item_total: {
                                    currency_code: total.currency,
                                    value: total.amount,
                                },
                            },
                        },
                        payer: {
                            name: {
                                given_name: firstName,
                                surname: lastName,
                            },
                        },
                        items: items.map(({ name, price, quantity }) => ({
                            name,
                            unit_amount: {
                                currency_code: price.currency,
                                value: price.amount,
                            },
                            quantity,
                        })),
                    },
                ],
            }),
        [total]
    );

    const onApprove = useCallback(
        async (data, actions) => {
            const details = await actions.order.capture();
            await onPay('paypal', details);
        },
        [onPay]
    );

    return (
        <div className={styles.root}>
            <ThemedButton
                fullWidth
                beforeIcon={<Icon size={24} />}
                theme={contentStyle}
                disabled={disabled || !isSupportedCurrency}
                className={styles.overlapButton}
                contentAlignLeft
            >
                {__t('payWith', { provider: label })}
            </ThemedButton>

            {isSupportedCurrency && (
                <PayPalScriptProvider options={{ 'client-id': payments.paypal.clientId, currency: total.currency }}>
                    <PayPalButtons
                        style={{ layout: 'horizontal', tagline: false }}
                        disabled={disabled}
                        createOrder={createOrder}
                        onApprove={onApprove}
                    />
                </PayPalScriptProvider>
            )}
        </div>
    );
}

PayPalPayment.propTypes = propTypes;
